import React, { useMemo, useState } from 'react';
import Moment from 'moment';
import { EstadoRespuesta, ObtenerEncuestaResponse, TipoEncuesta } from '@solimat/solimat-web-endpoint/lib/survey-ingestion/dto';
import { useGeneralManagementState } from 'components/pages/GeneralManagement/generalManagementState';
import useGetRole from 'hooks/useGetRole';
import './SurveysGrid.scss';

const PAGE_SIZE = 9;

interface ISurveysGrid {
  arrData: ObtenerEncuestaResponse[] | undefined;
  props: any;
}

const BADGE_VARIANT: Record<string, string> = {
  'Activa':        'active',
  'Enviada':       'sent',
  'Caducada':      'expired',
  'Sin contestar': 'pending',
  'Incompleta':    'incomplete',
};

const SurveysGrid: React.FC<ISurveysGrid> = ({ arrData }) => {
  const { isImpersonation } = useGetRole;
  const [currentPage, setCurrentPage] = useState(1);
  const { dispatch } = useGeneralManagementState();

  const formattedArrData = useMemo(() => {
    const source = arrData ?? [];
    return source.map((survey: ObtenerEncuestaResponse) => {
      const hasExpiryDate = !!survey.fechaCaducidadEfectiva;
      const expiryDate = hasExpiryDate
        ? (survey.fechaCaducidadEfectiva instanceof Date
          ? survey.fechaCaducidadEfectiva
          : new Date(`${survey.fechaCaducidadEfectiva}Z`))
        : null;

      return {
        ...survey,
        tipoFormateado: TipoEncuesta[survey.tipo],
        fechaCaducidadEfectivaFormateada: expiryDate,
        fechaCaducidadTexto: expiryDate
          ? Moment(expiryDate).format('DD/MM/YYYY HH:mm')
          : 'Sin caducidad',
        estadoFormateado: (isImpersonation()
          ? (survey.caducada
            ? 'Caducada'
            : survey.estadoRespuesta === EstadoRespuesta.Vacia
              ? 'Sin contestar'
              : survey.estadoRespuesta === EstadoRespuesta.Rellena
                ? 'Incompleta'
                : survey.estadoRespuesta === EstadoRespuesta.Enviada
                  ? 'Enviada'
                  : EstadoRespuesta[survey.estadoRespuesta])
          : (survey.caducada
            ? 'Caducada'
            : survey.estadoRespuesta === EstadoRespuesta.Vacia || survey.estadoRespuesta === EstadoRespuesta.Rellena
              ? 'Activa'
              : survey.estadoRespuesta === EstadoRespuesta.Enviada
                ? 'Enviada'
                : EstadoRespuesta[survey.estadoRespuesta])
        ),
        respuestasRellenas: `${survey.categoriasPreguntas?.flatMap(c => c.preguntas).filter(p => p.opciones.some(o => o.respondida && (!o.permiteTextoLibre || (o.permiteTextoLibre && o.textoLibre && o.textoLibre.length > 0)))).length ?? 0}/${survey.categoriasPreguntas?.flatMap(c => c.preguntas).length ?? 0}`,
        vistasTotales: survey.limiteOportunidades
          ? `${survey.numeroOportunidades}/${survey.limiteOportunidades}`
          : `${survey.numeroOportunidades}`,
      };
    });
  }, [arrData]);

  const totalPages = Math.ceil(formattedArrData.length / PAGE_SIZE);
  const pageItems = formattedArrData.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const handleCardClick = (item: any) => {
    if (!item.caducada && !isImpersonation()) {
      dispatch({ type: 'SHOW_SURVEY', payload: { show: true, id: item.asignacionId } });
    }
  };

  return (
    <div>
      <div className="surveys-grid">
        {pageItems.map((item, index) => {
          const isClickable = !item.caducada && !isImpersonation();
          const badgeVariant = BADGE_VARIANT[item.estadoFormateado] ?? 'default';
          const cardAriaLabel = isClickable
            ? `Abrir encuesta ${item.titulo}. Estado ${item.estadoFormateado}. Caduca ${item.fechaCaducidadTexto}`
            : `Encuesta ${item.titulo} no disponible. Estado ${item.estadoFormateado}`;

          return (
            <button
              type="button"
              key={item.asignacionId ?? index}
              className={[
                'survey-card',
                isClickable ? 'survey-card--clickable' : 'survey-card--disabled',
                item.estadoFormateado === 'Enviada'  ? 'survey-card--sent'    : '',
                item.estadoFormateado === 'Caducada' ? 'survey-card--expired' : '',
              ].join(' ').trim()}
              aria-label={cardAriaLabel}
              disabled={!isClickable}
              onClick={() => handleCardClick(item)}
            >
              <div className="survey-card__header">
                <span className={`survey-card__badge survey-card__badge--${badgeVariant}`}>
                  {item.estadoFormateado}
                </span>
                <span className="survey-card__type">{item.tipoFormateado}</span>
              </div>

              <h3 className="survey-card__title">{item.titulo}</h3>

              <div className="survey-card__meta">
                {item.expedienteId && (
                  <span className="survey-card__meta-item">
                    <span className="survey-card__meta-label">Expediente</span>
                    <span className="survey-card__meta-value">{item.expedienteId}</span>
                  </span>
                )}
                <span className="survey-card__meta-item">
                  <span className="survey-card__meta-label">Caduca</span>
                  <span className="survey-card__meta-value">
                    {item.fechaCaducidadTexto}
                  </span>
                </span>
              </div>

              {isImpersonation() && (
                <div className="survey-card__stats">
                  <div>
                    <span className="survey-card__stat-label">Respuestas</span>
                    <span className="survey-card__stat-value">{item.respuestasRellenas}</span>
                  </div>
                  <div>
                    <span className="survey-card__stat-label">Vistas</span>
                    <span className="survey-card__stat-value">{item.vistasTotales}</span>
                  </div>
                </div>
              )}
            </button>
          );
        })}
      </div>

      {totalPages > 1 && (
        <div className="surveys-pagination">
          <button
            className="surveys-pagination__btn"
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            aria-label="Página anterior"
          >
            ‹
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
            <button
              key={page}
              className={`surveys-pagination__btn${page === currentPage ? ' surveys-pagination__btn--active' : ''}`}
              onClick={() => setCurrentPage(page)}
              aria-label={`Ir a la página ${page}`}
              aria-current={page === currentPage ? 'page' : undefined}
            >
              {page}
            </button>
          ))}
          <button
            className="surveys-pagination__btn"
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            aria-label="Página siguiente"
          >
            ›
          </button>
        </div>
      )}
    </div>
  );
};

export default SurveysGrid;
